<template>
    <div class="common-table-container" :style="tableStyle">
        <table>
            <thead>
                <tr>
                    <th
                        v-for="(column, index) in columns"
                        :key="index"
                        :class="getColumnClass(column)"
                        :style="getHeaderStyle(column)"
                    >
                        {{ column.label }}
                    </th>
                </tr>
            </thead>
            <tbody>
                <tr
                    v-for="(row, rowIndex) in data"
                    :key="rowIndex"
                    :class="{
                        'highlighted-row': highlightRow && row.loadSection === highlightRow,
                        'clickable-row': rowClickable,
                    }"
                    @click="handleRowClick(row, rowIndex)"
                >
                    <td
                        v-for="(column, colIndex) in columns"
                        :key="colIndex"
                        :class="getCellClass(column, row)"
                        :style="getCellStyle(column, row)"
                    >
                        <template v-if="column.slot">
                            <slot :name="column.slot" :row="row" :value="row[column.key]"></slot>
                        </template>
                        <template v-else>
                            {{ formatCellValue(column, row[column.key]) }}
                        </template>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</template>
<script>
export default {
    name: "CommonTable",
    props: {
        data: {
            type: Array,
            default: () => [],
            required: true,
        },
        columns: {
            type: Array,
            default: () => [],
            required: true,
        },
        maxHeight: {
            type: String,
            default: "300px",
        },
        highlightRow: {
            type: String,
            default: "",
        },
        rowClickable: {
            type: Boolean,
            default: false,
        },
        tableStyle: {
            type: Object,
            default: () => ({}),
        },
        headerStyle: {
            type: Object,
            default: () => ({}),
        },
        cellStyle: {
            type: Object,
            default: () => ({}),
        },
    },
    methods: {
        formatCellValue(column, value) {
            if (value === 0 || value === null || value === undefined) {
                return "-";
            }

            if (column.formatter) {
                return column.formatter(value);
            }

            if (column.type === "number") {
                const num = Number(value);
                return isNaN(num) ? "-" : num.toLocaleString();
            }

            return value;
        },
        getColumnClass(column) {
            const classes = [];

            if (column.align) {
                classes.push(`text-${column.align}`);
            }

            if (column.headerClass) {
                classes.push(column.headerClass);
            }

            return classes.join(" ");
        },
        getCellClass(column, row) {
            const classes = [];

            if (column.align) {
                classes.push(`text-${column.align}`);
            }

            if (column.cellClass) {
                if (typeof column.cellClass === "function") {
                    const cellClass = column.cellClass(row);
                    if (cellClass) {
                        classes.push(cellClass);
                    }
                } else {
                    classes.push(column.cellClass);
                }
            }

            if (column.type === "number") {
                classes.push("number-cell");
            }

            return classes.join(" ");
        },
        handleRowClick(row, rowIndex) {
            if (this.rowClickable) {
                this.$emit("row-click", row, rowIndex);
            }
        },
        getHeaderStyle(column) {
            const style = { ...this.headerStyle };
            if (column.headerStyle) {
                Object.assign(style, column.headerStyle);
            }
            return style;
        },
        getCellStyle(column, row) {
            const style = { ...this.cellStyle };
            if (column.cellStyle) {
                if (typeof column.cellStyle === "function") {
                    Object.assign(style, column.cellStyle(row));
                } else {
                    Object.assign(style, column.cellStyle);
                }
            }
            return style;
        },
    },
};
</script>

<style scoped>
.common-table-container {
    /* height: 100%; */
    max-height: v-bind(maxHeight);
    overflow-y: auto;
    /* flex: 1; */
    background: #ffffff;
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    border: 1px solid #e2e8f0;
}

.common-table-container::-webkit-scrollbar {
    width: 6px;
}

.common-table-container::-webkit-scrollbar-track {
    background: transparent;
}

.common-table-container::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
}

.common-table-container::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
}

table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    background: white;
    font-size: 12px;
}

th,
td {
    padding: 9px 16px;
    text-align: left;
    border-bottom: 1px solid #f1f5f9;
}

th {
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    color: #475569;
    font-weight: 600;
    text-transform: none;
    letter-spacing: 0;
    position: sticky;
    top: 0;
    z-index: 10;
    border-bottom: 2px solid #e2e8f0;
}

td {
    color: #334155;
    font-weight: 500;
    transition: all 0.15s ease;
}

tr:hover td {
    background: #f8fafc;
    color: #1e293b;
}

.clickable-row {
    cursor: pointer;
    transition: all 0.2s ease;
}

.clickable-row:active {
    background: #f1f5f9;
}

tr:last-child td {
    border-bottom: none;
}

/* 정렬 클래스 */
.text-left {
    text-align: left;
}

.text-center {
    text-align: center;
}

.text-right {
    text-align: right;
}

/* 숫자 셀 스타일 */
.number-cell {
    font-weight: 600;
}

/* 행 구분선 */
tr:nth-child(even) td {
    background: #fafbfc;
}

tr:nth-child(even):hover td {
    background: #f1f5f9;
}

/* 하이라이트된 행 스타일 */
.highlighted-row td {
    background: linear-gradient(135deg, #e0e7ef 0%, #d1d8e0 100%) !important;
    color: #1e293b;
    font-weight: 600;
}

.highlighted-row:hover td {
    background: linear-gradient(135deg, #e0e7ef 0%, #d1d8e0 100%) !important;
    color: #1e293b;
}
</style>
